import { EChartOption } from 'echarts'
import {
  FC,
  MouseEvent,
  ReactNode,
  SVGProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { EChart } from 'src/charts/EChart'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { Skeleton } from 'src/components/Skeleton'
import { Tooltip } from 'src/components/Tooltip'
import { TrendBadge } from 'src/components/TrendBadge'
import { ReactComponent as BookingIcon } from 'src/assets/svg/booking-brand.svg'
import { ReactComponent as TopCampaignsIcon } from 'src/assets/svg/camp-card.svg'
import { ReactComponent as ExpediaIcon } from 'src/assets/svg/expedia-brand.svg'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/Globe.svg'
import { ReactComponent as TotalChatsIcon } from 'src/assets/svg/inbox-analysis-total-chats.svg'
import { ReactComponent as PlatformComparisonIcon } from 'src/assets/svg/inbox-analysis-platform-comparison.svg'
import { ReactComponent as TopicIcon } from 'src/assets/svg/inbox-analysis-top-topic.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as TotalMessagesIcon } from 'src/assets/svg/total-messages-inbox-analysis.svg'
import { ReactComponent as TotalSourceIcon } from 'src/assets/svg/total-source-global.svg'
import { ReactComponent as NewVsReturningIcon } from 'src/assets/svg/ic-round-person-2.svg'
import { ReactComponent as TotalUsersIcon } from 'src/assets/svg/solar_user-outline.svg'
import { ReactComponent as WhatsappIcon } from 'src/assets/svg/whatsapp.svg'
import { InboxAnalysisPage } from 'src/reactQuery/useGetInboxAnalysisPagesQuery'
import { useGetInboxAvailablePlatformsQuery } from 'src/reactQuery/useGetInboxAvailablePlatformsQuery'
import {
  InboxOverviewSentimentBreakdown,
  InboxOverviewSourceItem,
  useGetInboxOverviewSummaryQuery,
} from 'src/reactQuery/useGetInboxOverviewSummaryQuery'
import classes from './inboxOverview.module.scss'

// From inboxOverview.module.scss: .platformChartCard is min-height 296 / radius
// 12. The skeleton matches it so the card holds its real height during load —
// otherwise .emptyCard (min-height 180) kicks in and the card collapses.
const PLATFORM_CHART_CARD_HEIGHT = 296
const PLATFORM_CHART_CARD_RADIUS = 12

const SENTIMENT_COLORS = {
  negative: '#FF6833',
  neutral: '#C7CED1',
  positive: '#8080FF',
}

const PLATFORM_COLORS: Record<string, string> = {
  booking: '#0A4FA3',
  expedia: '#F5D328',
  facebook: '#5368B9',
  instagram: '#EF5451',
  whatsapp: '#6CAB82',
}

const PLATFORM_OPTIONS = [
  { id: 'all', label: 'All Platforms' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'booking', label: 'Booking' },
  { id: 'expedia', label: 'Expedia' },
]

const PLATFORM_ICON_MAP: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  booking: BookingIcon,
  expedia: ExpediaIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  whatsapp: WhatsappIcon,
}

const PLATFORM_CHART_ICON_CLASS_MAP: Record<string, string> = {
  // booking/expedia svgs carry their own brand colors — no class needed
  facebook: 'facebookChartIcon',
  instagram: 'instagramChartIcon',
  whatsapp: 'whatsappChartIcon',
}

// Top Source icon: tint the box + icon per platform (transparent, light),
// mirroring the platform colors used in the Platform Comparison chart.
const TOP_SOURCE_BOX_CLASS_MAP: Record<string, string> = {
  facebook: classes.topSourceIconBoxFacebook,
  instagram: classes.topSourceIconBoxInstagram,
  whatsapp: classes.topSourceIconBoxWhatsapp,
}

const TOP_SOURCE_ICON_CLASS_MAP: Record<string, string> = {
  facebook: classes.topSourceIconFacebook,
  instagram: classes.topSourceIconInstagram,
  whatsapp: classes.topSourceIconWhatsapp,
}

interface InboxOverviewProps {
  // The page's own pages query. `pages` is [] while it is in flight, which is
  // indistinguishable from having none — and the fallback aggregations below
  // would quietly render a zeroed chart off that empty array. Folded into
  // isInitialLoading so the widget shows skeletons instead.
  arePagesLoading?: boolean
  fromDate?: string
  pages: InboxAnalysisPage[]
  toDate?: string
}

interface SentimentTotals {
  negative: number
  neutral: number
  positive: number
  total: number
}

interface MetricCardProps {
  // "vs previous period" TrendBadge next to the total.
  badge?: ReactNode
  icon: FC<SVGProps<SVGSVGElement>>
  isLoading?: boolean
  title: string
  totals: SentimentTotals
}

interface PlatformComparisonItem {
  id: string
  label: string
  negative: number
  neutral: number
  positive: number
  total: number
}

type SegmentType = 'negative' | 'neutral' | 'positive'

interface PlatformTooltipState extends PlatformComparisonItem {
  hoveredSegment: SegmentType
  x: number
  y: number
}

interface TopTopicItem {
  messages: number
  negative: number
  neutral: number
  positive: number
  title: string
}

interface TopCampaignItem {
  chats: number
  chatsProgress: number
  messages: number
  messagesProgress: number
  title: string
}

interface TopSourceStat {
  label: string
  value: string
}

interface ResponseTimeBreakdownItem {
  color: string
  label: string
  percentage: number
}

interface TopSourceTopic {
  className: 'negativeTopicPill' | 'neutralTopicPill' | 'positiveTopicPill'
  label: string
}

interface TopSourceDetailsData {
  chats: number
  negative: number
  neutral: number
  positive: number
  sourceId: string
  sourceName: string
  sourceRankLabel: string
  stats: TopSourceStat[]
  topics: TopSourceTopic[]
}

const emptyTotals: SentimentTotals = {
  negative: 0,
  neutral: 0,
  positive: 0,
  total: 0,
}

const formatPercent = (value: number, total: number) => {
  if (!total) return 0
  return Math.round((value / total) * 100)
}

// booking/expedia brand svgs render black & white in the source lists
// (they keep brand colors on the comparison chart labels)
const MONO_BRAND_PLATFORMS = new Set(['booking', 'expedia'])

const getPlatformIcon = (platform: string) => {
  const key = platform.toLowerCase()
  const Icon = PLATFORM_ICON_MAP[key] ?? GlobeIcon
  const className = MONO_BRAND_PLATFORMS.has(key)
    ? `${classes.sourceIcon} ${classes.monoBrandIcon}`
    : classes.sourceIcon
  return <Icon className={className} />
}

const getPlatformChartIcon = (platform: string) => {
  const Icon = PLATFORM_ICON_MAP[platform.toLowerCase()] ?? GlobeIcon
  const colorClass = PLATFORM_CHART_ICON_CLASS_MAP[platform.toLowerCase()]

  return (
    <span
      className={`${classes.platformChartIcon} ${
        colorClass ? classes[colorClass] : ''
      }`}
    >
      <Icon />
    </span>
  )
}

const getPlatformLabel = (platform: string) => {
  const option = PLATFORM_OPTIONS.find((item) => item.id === platform)
  if (option) return option.label
  return platformDisplayLabel(
    platform,
    platform.replace(/\b\w/g, (char) => char.toUpperCase())
  )
}

const aggregateTotals = (
  pages: InboxAnalysisPage[],
  key: 'conversations' | 'messages'
): SentimentTotals =>
  pages.reduce<SentimentTotals>(
    (acc, page) => ({
      negative: acc.negative + page[key].negative,
      neutral: acc.neutral + page[key].neutral,
      positive: acc.positive + page[key].positive,
      total: acc.total + page[key].total,
    }),
    { ...emptyTotals }
  )

const mapApiSentimentTotals = (
  sentiment?: InboxOverviewSentimentBreakdown
): SentimentTotals => ({
  negative: sentiment?.negative.count ?? 0,
  neutral: sentiment?.neutral.count ?? 0,
  positive: sentiment?.positive.count ?? 0,
  total: sentiment?.total ?? 0,
})

const mapApiSourceToComparisonItem = (
  source: InboxOverviewSourceItem,
  metric: 'chats' | 'messages'
): PlatformComparisonItem => {
  const sentiment =
    metric === 'chats'
      ? source.sentiment
      : source.messageSentiment ?? source.sentiment

  return {
    id: source.id,
    label: source.name,
    negative: sentiment.negative.count,
    neutral: sentiment.neutral.count,
    positive: sentiment.positive.count,
    total: metric === 'chats' ? source.chats : source.messages,
  }
}

const formatCompactNumber = (value: number) => {
  if (value >= 1000000000)
    return `${(value / 1000000000).toFixed(1).replace('.0', '')}B`
  if (value >= 1000000)
    return `${(value / 1000000).toFixed(1).replace('.0', '')}M`
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace('.0', '')}K`
  return value.toLocaleString()
}

const formatCardTotal = (value: number) => {
  if (value >= 1000000) return formatCompactNumber(value)
  return value.toLocaleString()
}

const clampPercent = (value: number) => Math.min(Math.max(value, 0), 100)

const formatResponseTime = (minutes: number) => {
  if (!minutes) return '0m'
  if (minutes < 60) return `${minutes}m`

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

const getDominantSentiment = (item: TopTopicItem) => {
  const sentiments = [
    {
      color: SENTIMENT_COLORS.positive,
      label: 'Mostly Positive',
      value: item.positive,
    },
    {
      color: SENTIMENT_COLORS.neutral,
      label: 'Mostly Neutral',
      value: item.neutral,
    },
    {
      color: SENTIMENT_COLORS.negative,
      label: 'Mostly Negative',
      value: item.negative,
    },
  ]

  return sentiments.reduce((winner, current) =>
    current.value > winner.value ? current : winner
  )
}

// Round up to a "nice" axis ceiling (1/2/5 * 10^n) so the scale tracks the
// real data instead of a fixed floor — small totals (e.g. a handful of chats)
// stay readable instead of being crushed against a hard-coded 9000 top.
const getNiceMax = (maxValue: number) => {
  if (maxValue <= 0) return 0
  const magnitude = 10 ** Math.floor(Math.log10(maxValue))
  const normalized = maxValue / magnitude
  const niceNormalized =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return niceNormalized * magnitude
}

const getYAxisLabels = (maxValue: number) => {
  const roundedMax = getNiceMax(maxValue)
  if (!roundedMax) return [4, 3, 2, 1, 0]
  return [
    roundedMax,
    Math.round(roundedMax * 0.75),
    Math.round(roundedMax * 0.5),
    Math.round(roundedMax * 0.25),
    0,
  ]
}

const ClockIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="12" r="9.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M12 6.75V12l3.5 2.1"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

const PlatformComparisonChart: FC<{
  items: PlatformComparisonItem[]
  metricLabel: string
}> = ({ items, metricLabel }) => {
  const [tooltip, setTooltip] = useState<PlatformTooltipState | null>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const chartHeight = 175
  const minVisibleSegmentHeight = 3
  const maxTotal = Math.max(...items.map((item) => item.total), 0)
  const chartMax = getNiceMax(maxTotal) || 1
  const yAxisLabels = getYAxisLabels(maxTotal)
  const visibleItems = items.filter((item) => item.total > 0)

  const updateTooltipPosition = (
    event: MouseEvent,
    item: PlatformComparisonItem,
    segment: SegmentType
  ) => {
    const rect = chartRef.current?.getBoundingClientRect()
    if (!rect) return

    setTooltip({
      ...item,
      hoveredSegment: segment,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    })
  }

  const hideTooltip = () => setTooltip(null)

  const segmentMeta: Record<SegmentType, { color: string; label: string }> = {
    negative: {
      color: SENTIMENT_COLORS.negative,
      label: `Negative ${metricLabel}`,
    },
    neutral: {
      color: SENTIMENT_COLORS.neutral,
      label: `Neutral ${metricLabel}`,
    },
    positive: {
      color: SENTIMENT_COLORS.positive,
      label: `Positive ${metricLabel}`,
    },
  }

  return (
    <div className={classes.platformChartCard} ref={chartRef}>
      {tooltip && (
        <div
          className={classes.platformTooltip}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className={classes.platformTooltipHeader}>
            <span className={classes.platformTooltipIcon}>
              {getPlatformIcon(tooltip.id)}
            </span>
            <span className={classes.platformTooltipPlatform}>
              {tooltip.label}
            </span>
          </div>
          <div className={classes.platformTooltipRow}>
            <span className={classes.platformTooltipLabel}>
              Total {metricLabel}:
            </span>
            <span className={classes.platformTooltipValue}>
              {tooltip.total.toLocaleString()}
            </span>
          </div>
          <div className={classes.platformTooltipRow}>
            <span
              className={classes.platformTooltipDot}
              style={{
                background: segmentMeta[tooltip.hoveredSegment].color,
              }}
            />
            <span className={classes.platformTooltipLabel}>
              {segmentMeta[tooltip.hoveredSegment].label}:
            </span>
            <span className={classes.platformTooltipValue}>
              {tooltip[tooltip.hoveredSegment].toLocaleString()}
            </span>
          </div>
        </div>
      )}

      <div className={classes.platformChartArea}>
        <div className={classes.platformYAxis}>
          {yAxisLabels.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>

        <div className={classes.platformPlot}>
          <div className={classes.gridLines}>
            {yAxisLabels.slice(0, -1).map((label) => (
              <span key={label} />
            ))}
          </div>

          <div className={classes.platformBars}>
            {visibleItems.map((item) => {
              // strictly proportional segment heights (tiny visibility floor
              // only); value labels render on segments tall enough to fit
              const getSegmentHeight = (value: number) => {
                if (!value) return 0
                return Math.max(
                  (value / chartMax) * chartHeight,
                  minVisibleSegmentHeight
                )
              }

              const positiveHeight = getSegmentHeight(item.positive)
              const negativeHeight = getSegmentHeight(item.negative)
              const neutralHeight = getSegmentHeight(item.neutral)

              // stack renders column-reverse (positive at the bottom, neutral
              // on top) — round whichever segment is visually topmost
              const topSegment: SegmentType = neutralHeight
                ? 'neutral'
                : negativeHeight
                  ? 'negative'
                  : 'positive'
              const segmentClass = (segment: SegmentType, base: string) =>
                segment === topSegment ? `${base} ${classes.segmentTop}` : base

              return (
                <div className={classes.platformColumn} key={item.id}>
                  <div
                    className={classes.platformStack}
                    style={{ height: chartHeight }}
                  >
                    <span
                      className={segmentClass(
                        'positive',
                        classes.positiveSegment
                      )}
                      onMouseEnter={(event) =>
                        updateTooltipPosition(event, item, 'positive')
                      }
                      onMouseLeave={hideTooltip}
                      onMouseMove={(event) =>
                        updateTooltipPosition(event, item, 'positive')
                      }
                      style={{ height: positiveHeight }}
                    >
                      {positiveHeight >= 16 && item.positive.toLocaleString()}
                    </span>
                    <span
                      className={segmentClass(
                        'negative',
                        classes.negativeSegment
                      )}
                      onMouseEnter={(event) =>
                        updateTooltipPosition(event, item, 'negative')
                      }
                      onMouseLeave={hideTooltip}
                      onMouseMove={(event) =>
                        updateTooltipPosition(event, item, 'negative')
                      }
                      style={{ height: negativeHeight }}
                    >
                      {negativeHeight >= 16 && item.negative.toLocaleString()}
                    </span>
                    <span
                      className={segmentClass(
                        'neutral',
                        classes.neutralSegment
                      )}
                      onMouseEnter={(event) =>
                        updateTooltipPosition(event, item, 'neutral')
                      }
                      onMouseLeave={hideTooltip}
                      onMouseMove={(event) =>
                        updateTooltipPosition(event, item, 'neutral')
                      }
                      style={{ height: neutralHeight }}
                    >
                      {neutralHeight >= 16 && item.neutral.toLocaleString()}
                    </span>
                  </div>
                  <div className={classes.platformChartLabel}>
                    {getPlatformChartIcon(item.id)}
                    <span>{item.label}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className={classes.platformLegend}>
        <span>
          <i className={classes.positiveLegend} />
          Positive
        </span>
        <span>
          <i className={classes.neutralLegend} />
          Neutral
        </span>
        <span>
          <i className={classes.negativeLegend} />
          Negative
        </span>
      </div>
    </div>
  )
}

const MetricCard: FC<MetricCardProps> = ({
  badge,
  icon: Icon,
  isLoading = false,
  title,
  totals,
}) => {
  const rows = [
    {
      color: SENTIMENT_COLORS.positive,
      count: totals.positive,
      label: 'Positive',
      percentage: formatPercent(totals.positive, totals.total),
    },
    {
      color: SENTIMENT_COLORS.neutral,
      count: totals.neutral,
      label: 'Neutral',
      percentage: formatPercent(totals.neutral, totals.total),
    },
    {
      color: SENTIMENT_COLORS.negative,
      count: totals.negative,
      label: 'Negative',
      percentage: formatPercent(totals.negative, totals.total),
    },
  ]

  return (
    <div className={classes.metricCard}>
      <div className={classes.cardHeader}>
        <div className={classes.cardTitle}>
          <Icon className={classes.cardIcon} />
          <span>{title}</span>
        </div>
        {isLoading ? (
          <Skeleton height={28} width={72} />
        ) : (
          <div className={classes.cardValueGroup}>
            {badge}
            <strong className={classes.cardValue}>
              {formatCardTotal(totals.total)}
            </strong>
          </div>
        )}
      </div>

      <div className={classes.sentimentRows}>
        {isLoading
          ? rows.map((row) => (
              <div className={classes.sentimentRow} key={row.label}>
                <Skeleton height={18} />
              </div>
            ))
          : rows.map((row) => (
              <div className={classes.sentimentRow} key={row.label}>
                <div className={classes.barTrack}>
                  <span
                    className={classes.barFill}
                    style={{
                      backgroundColor: row.color,
                      width: `${row.percentage}%`,
                    }}
                  />
                </div>
                <div className={classes.sentimentMeta}>
                  <span className={classes.sentimentPercent}>
                    {row.percentage}%
                  </span>
                  <span>{row.label}</span>
                </div>
                <span className={classes.sentimentCount}>
                  {row.count.toLocaleString()}
                </span>
              </div>
            ))}
      </div>
    </div>
  )
}

const AUDIENCE_COLORS = {
  new: '#8080FF',
  organic: '#2F7D6D',
  paid: '#C8641F',
  returning: '#6B6F7B',
}

interface SplitSegment {
  color: string
  count: number
  label: string
}

interface SplitMetricCardProps {
  // "vs previous period" TrendBadge next to the total.
  badge?: ReactNode
  icon: FC<SVGProps<SVGSVGElement>>
  isLoading?: boolean
  segments?: SplitSegment[]
  title: string
  total: number | null
}

// Second-row card: headline total, one stacked bar, and a legend with count
// and share per segment (Organic vs Paid Chats / New vs Returning Users).
const SplitMetricCard: FC<SplitMetricCardProps> = ({
  badge,
  icon: Icon,
  isLoading = false,
  segments,
  title,
  total,
}) => {
  const hasData = total !== null
  const segmentTotal = (segments ?? []).reduce((sum, s) => sum + s.count, 0)

  const isSolo = !segments

  return (
    <div className={`${classes.metricCard} ${classes.splitMetricCard}`}>
      <div className={classes.cardHeader}>
        <div className={`${classes.cardTitle} ${classes.splitMetricTitle}`}>
          <Icon className={classes.cardIcon} />
          <span>{title}</span>
        </div>
        {isSolo ? null : isLoading ? (
          <Skeleton height={28} width={72} />
        ) : hasData ? (
          <div className={classes.cardValueGroup}>
            {badge}
            <strong className={classes.cardValue}>
              {formatCardTotal(total)}
            </strong>
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div className={classes.splitBody}>
          {isSolo ? (
            <Skeleton height={36} width={132} />
          ) : (
            <>
              <Skeleton height={12} radius={6} />
              <Skeleton height={36} />
            </>
          )}
        </div>
      ) : !hasData ? (
        <div className={classes.metricNoData}>No Data</div>
      ) : isSolo ? (
        <div className={classes.splitSoloValue}>
          <strong>{formatCardTotal(total)}</strong>
          {badge}
        </div>
      ) : (
        <div className={classes.splitBody}>
          <div className={classes.splitBar}>
            {segments.map((segment) => (
              <span
                className={classes.splitBarSegment}
                key={segment.label}
                style={{
                  backgroundColor: segment.color,
                  width: `${formatPercent(segment.count, segmentTotal)}%`,
                }}
              />
            ))}
          </div>
          <div className={classes.splitLegend}>
            {segments.map((segment) => (
              <div className={classes.splitLegendItem} key={segment.label}>
                <i
                  className={classes.splitLegendDot}
                  style={{ backgroundColor: segment.color }}
                />
                <span className={classes.splitLegendLabel}>
                  {segment.label}
                </span>
                <strong>{segment.count.toLocaleString()}</strong>
                <span className={classes.splitLegendPercent}>
                  {formatPercent(segment.count, segmentTotal)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const capitalizeFirstLetter = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value

const TopTopicList: FC<{ items: TopTopicItem[] }> = ({ items }) => (
  <div className={classes.topTopicList}>
    {items.slice(0, 6).map((topic, index) => {
      const dominantSentiment = getDominantSentiment(topic)
      const topicTitle = capitalizeFirstLetter(topic.title)

      return (
        <div className={classes.topTopicItem} key={topic.title}>
          <div className={classes.topTopicRow}>
            <div className={classes.topicTitleGroup}>
              <span className={classes.topicRank}>{index + 1}</span>
              <span className={classes.topicTitle} title={`# ${topicTitle}`}>
                # {topicTitle}
              </span>
            </div>

            <div className={classes.topicStatsGroup}>
              <div className={classes.topicSentiment}>
                <span
                  className={classes.topicSentimentLabel}
                  style={{ color: dominantSentiment.color }}
                >
                  {dominantSentiment.label}
                </span>
                <div className={classes.topicSentimentBar}>
                  <span
                    className={classes.topicPositive}
                    style={{ width: `${clampPercent(topic.positive)}%` }}
                  />
                  <span
                    className={classes.topicNeutral}
                    style={{ width: `${clampPercent(topic.neutral)}%` }}
                  />
                  <span
                    className={classes.topicNegative}
                    style={{ width: `${clampPercent(topic.negative)}%` }}
                  />
                </div>
              </div>
              <span className={classes.topicMessageCount}>
                {formatCompactNumber(topic.messages)} Messages
              </span>
            </div>
          </div>
        </div>
      )
    })}
  </div>
)

// Mirrors TopTopicList exactly: 6 rows in the same .topTopicItem wrappers, so
// the card holds its final height and nothing shifts when the topics land.
// Dimensions come straight from inboxOverview.module.scss — .topicRank is
// 28x28 (border-radius 37px => a circle), .topicTitle's 16px font on a 1.5
// line-height, .topicSentiment is 119x24, and .topicMessageCount's 12px text
// sits in a 112px-min column (84 keeps the block inside its own width).
const TopTopicSkeleton: FC = () => (
  <div className={classes.topTopicList}>
    {Array.from({ length: 6 }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className={classes.topTopicItem} key={index}>
        <div className={classes.topTopicRow}>
          <div className={classes.topicTitleGroup}>
            <Skeleton circle width={28} />
            <Skeleton height={16} width={140} />
          </div>
          <div className={classes.topicStatsGroup}>
            <Skeleton height={24} width={119} />
            <Skeleton height={16} width={84} />
          </div>
        </div>
      </div>
    ))}
  </div>
)

const TopCampaignsList: FC<{ items: TopCampaignItem[] }> = ({ items }) => (
  <div className={classes.topCampaignsList}>
    {items.slice(0, 3).map((campaign, index) => (
      <div className={classes.topCampaignItem} key={campaign.title}>
        <span className={classes.topicRank}>{index + 1}</span>
        <div className={classes.campaignContent}>
          <h3 className={classes.campaignTitle} title={campaign.title}>
            {campaign.title}
          </h3>

          <div className={classes.campaignMetrics}>
            <div className={classes.campaignMetric}>
              <div className={classes.campaignMetricHeader}>
                <span>Messages</span>
                <strong>{formatCompactNumber(campaign.messages)}</strong>
              </div>
              <div className={classes.campaignProgressTrack}>
                <span
                  className={classes.campaignMessagesProgress}
                  style={{
                    width: `${clampPercent(campaign.messagesProgress)}%`,
                  }}
                />
              </div>
            </div>

            <div className={classes.campaignMetric}>
              <div className={classes.campaignMetricHeader}>
                <span>Chats</span>
                <strong>{formatCompactNumber(campaign.chats)}</strong>
              </div>
              <div className={classes.campaignProgressTrack}>
                <span
                  className={classes.campaignChatsProgress}
                  style={{ width: `${clampPercent(campaign.chatsProgress)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
)

// Mirrors TopCampaignsList: 3 .topCampaignItem rows, each a 28x28 rank circle
// (.topicRank) beside a title (16px/20px) and two metrics whose progress tracks
// are 12px tall with radius 20 (.campaignProgressTrack). Header text is 18px
// line-height on the left, 16px on the right (.campaignMetricHeader).
const TopCampaignsSkeleton: FC = () => (
  <div className={classes.topCampaignsList}>
    {Array.from({ length: 3 }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className={classes.topCampaignItem} key={index}>
        <Skeleton circle width={28} />
        <div className={classes.campaignContent}>
          <div className={classes.campaignTitle}>
            <Skeleton height={20} width={180} />
          </div>
          <div className={classes.campaignMetrics}>
            {Array.from({ length: 2 }).map((__, metricIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={classes.campaignMetric} key={metricIndex}>
                <div className={classes.campaignMetricHeader}>
                  <Skeleton height={18} width={64} />
                  <Skeleton height={16} width={40} />
                </div>
                <Skeleton height={12} radius={20} />
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
)

// Mirrors TopSourceDetails. Real dimensions from inboxOverview.module.scss:
// .topSourceIconBox 40x40 radius 8; .topSourceName 16px and .topSourceMeta 12px
// on 100% line-height; .topSourceStatCard min-height 64 radius 10 in a 4-column
// grid; .topSourceSectionTitle 15px; .topSourceLegend rows are 13px; the donut
// is a 90x90 circle; the topic pills are min-height 35 with radius 31.
const TopSourceSkeleton: FC = () => (
  <div className={classes.topSourceBody}>
    <div className={classes.topSourceIdentity}>
      <Skeleton height={40} width={40} />
      <div className={classes.topSourceText}>
        <Skeleton height={16} width={120} />
        <Skeleton height={12} width={84} />
      </div>
    </div>

    <div className={classes.topSourceStats}>
      {Array.from({ length: 4 }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Skeleton height={64} key={index} radius={10} />
      ))}
    </div>

    <div className={classes.sentimentBreakdown}>
      <Skeleton height={15} width={148} />
      <div className={classes.sentimentBreakdownContent}>
        <div className={classes.topSourceLegend}>
          {Array.from({ length: 3 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Skeleton height={13} key={index} width={104} />
          ))}
        </div>
        <div className={classes.topSourceDonut}>
          <Skeleton circle width={90} />
        </div>
      </div>
    </div>

    <div className={classes.topSourceDivider} />

    <div className={classes.topSourceTopics}>
      <Skeleton height={15} width={162} />
      <div className={classes.topSourceTopicPills}>
        {Array.from({ length: 3 }).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Skeleton height={35} key={index} radius={31} width="100%" />
        ))}
      </div>
    </div>
  </div>
)

// Mirrors ResponseTimeBreakdownCard's rows: the .responseTimeRow grid is
// 72px / minmax(72px, 1fr) / 34px, its label and percent are 12px text and the
// track is 8px tall with radius 20 (.responseTimeTrack).
const ResponseTimeSkeleton: FC = () => (
  <div className={classes.responseTimeRows}>
    {Array.from({ length: 3 }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className={classes.responseTimeRow} key={index}>
        <Skeleton height={12} width={64} />
        <div className={classes.responseTimeTrack}>
          <Skeleton height={8} radius={20} />
        </div>
        <Skeleton height={12} width={30} />
      </div>
    ))}
  </div>
)

const FacebookSourceIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path
      d="M22 16.8565C22 20.4965 19.83 22.6665 16.19 22.6665H15C14.45 22.6665 14 22.2165 14 21.6665V15.8965C14 15.6265 14.22 15.3965 14.49 15.3965L16.25 15.3665C16.39 15.3565 16.51 15.2565 16.54 15.1165L16.89 13.2065C16.92 13.0265 16.78 12.8565 16.59 12.8565L14.46 12.8865C14.18 12.8865 13.96 12.6665 13.95 12.3965L13.91 9.9465C13.91 9.7865 14.04 9.64652 14.21 9.64652L16.61 9.60651C16.78 9.60651 16.91 9.47652 16.91 9.30652L16.87 6.90649C16.87 6.73649 16.74 6.60651 16.57 6.60651L13.87 6.64652C12.21 6.67652 10.89 8.0365 10.92 9.6965L10.97 12.4465C10.98 12.7265 10.76 12.9465 10.48 12.9565L9.28 12.9765C9.11 12.9765 8.98001 13.1065 8.98001 13.2765L9.01001 15.1765C9.01001 15.3465 9.14 15.4765 9.31 15.4765L10.51 15.4565C10.79 15.4565 11.01 15.6765 11.02 15.9465L11.11 21.6465C11.12 22.2065 10.67 22.6665 10.11 22.6665H7.81C4.17 22.6665 2 20.4965 2 16.8465V8.4765C2 4.8365 4.17 2.6665 7.81 2.6665H16.19C19.83 2.6665 22 4.8365 22 8.4765V16.8565Z"
      fill="currentColor"
    />
  </svg>
)

// A "most mentioned topic" pill. Only shows the hover tooltip when the label
// is actually truncated (ellipsis visible); short labels that fit get no
// tooltip.
const MostMentionedTopicPill: FC<{ className: string; label: string }> = ({
  className,
  label,
}) => {
  const textRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const el = textRef.current
    if (!el) return undefined
    const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [label])

  const pill = (
    <span className={className}>
      <span className={classes.topicPillText} ref={textRef}>
        {label}
      </span>
    </span>
  )

  if (!isTruncated) return pill

  return (
    <Tooltip
      mouseEnterDelay={0.3}
      overlay={label}
      overlayClassName="topCampaignTooltip"
      placement="top"
    >
      {pill}
    </Tooltip>
  )
}

const TopSourceDetails: FC<{ data: TopSourceDetailsData }> = ({ data }) => {
  const sentimentChartOptions: EChartOption<EChartOption.SeriesPie> =
    useMemo(() => {
      const sourceSeries = {
        data: [
          { name: 'Positive', value: clampPercent(data.positive) },
          { name: 'Neutral', value: clampPercent(data.neutral) },
          { name: 'Negative', value: clampPercent(data.negative) },
        ],
        emphasis: {
          scale: false,
        },
        itemStyle: {
          borderColor: '#F3F6FF',
          borderWidth: 1,
        },
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        radius: ['68%', '90%'],
        type: 'pie',
      } as EChartOption.SeriesPie

      return {
        color: [
          SENTIMENT_COLORS.positive,
          SENTIMENT_COLORS.neutral,
          SENTIMENT_COLORS.negative,
        ],
        series: [sourceSeries],
        tooltip: {
          formatter: '{b}: {c}%',
          trigger: 'item',
        },
      }
    }, [data.negative, data.neutral, data.positive])

  return (
    <div className={classes.topSourceBody}>
      <div className={classes.topSourceIdentity}>
        {(() => {
          const platformKey = (data.sourceId || data.sourceName).toLowerCase()
          const boxClass = TOP_SOURCE_BOX_CLASS_MAP[platformKey] ?? ''
          const iconClass =
            TOP_SOURCE_ICON_CLASS_MAP[platformKey] ??
            classes.topSourceFacebookIcon
          const SourceIcon =
            platformKey === 'facebook'
              ? FacebookSourceIcon
              : PLATFORM_ICON_MAP[platformKey] ?? GlobeIcon
          return (
            <span className={`${classes.topSourceIconBox} ${boxClass}`}>
              <SourceIcon className={iconClass} />
            </span>
          )
        })()}
        <div className={classes.topSourceText}>
          <span className={classes.topSourceName} title={data.sourceName}>
            {data.sourceName}
          </span>
          <span className={classes.topSourceMeta} title={data.sourceRankLabel}>
            {data.sourceRankLabel}
          </span>
        </div>
      </div>

      <div className={classes.topSourceStats}>
        {data.stats.map((stat) => (
          <div className={classes.topSourceStatCard} key={stat.label}>
            <span title={stat.label}>{stat.label}</span>
            <strong title={stat.value}>{stat.value}</strong>
          </div>
        ))}
      </div>

      <div className={classes.sentimentBreakdown}>
        <span className={classes.topSourceSectionTitle}>
          Sentiment breakdown
        </span>
        <div className={classes.sentimentBreakdownContent}>
          <div className={classes.topSourceLegend}>
            <span>
              <i className={classes.positiveLegend} />
              {clampPercent(data.positive)}% Positive
            </span>
            <span>
              <i className={classes.neutralLegend} />
              {clampPercent(data.neutral)}% Neutral
            </span>
            <span>
              <i className={classes.negativeLegend} />
              {clampPercent(data.negative)}% Negative
            </span>
          </div>

          <div className={classes.topSourceDonut}>
            <div className={classes.topSourceDonutLabel}>
              <span>Chats</span>
              <strong>{formatCompactNumber(data.chats)}</strong>
            </div>
            <EChart height={90} options={sentimentChartOptions} width={90} />
          </div>
        </div>
      </div>

      <div className={classes.topSourceDivider} />

      <div className={classes.topSourceTopics}>
        <span className={classes.topSourceSectionTitle}>
          Most mentioned topics
        </span>
        <div className={classes.topSourceTopicPills}>
          {data.topics.map((topic) => (
            <MostMentionedTopicPill
              className={classes[topic.className as keyof typeof classes]}
              key={topic.label}
              label={topic.label}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const ResponseTimeBreakdownCard: FC<{
  isLoading: boolean
  items: ResponseTimeBreakdownItem[]
}> = ({ isLoading, items }) => (
  <div className={`${classes.metricCard} ${classes.responseTimeCard}`}>
    <div className={classes.cardHeader}>
      <div className={classes.responseTimeTitle}>
        <ClockIcon className={classes.responseTimeIcon} />
        <span>Response Time Breakdown</span>
      </div>
    </div>

    {isLoading ? (
      <ResponseTimeSkeleton />
    ) : (
      <div className={classes.responseTimeRows}>
        {items.map((row) => (
          <div className={classes.responseTimeRow} key={row.label}>
            <span className={classes.responseTimeLabel}>{row.label}</span>
            <div className={classes.responseTimeTrack}>
              <span
                className={classes.responseTimeFill}
                style={{
                  backgroundColor: row.color,
                  width: `${clampPercent(row.percentage)}%`,
                }}
              />
            </div>
            <span className={classes.responseTimePercent}>
              {clampPercent(row.percentage)}%
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
)

export const InboxOverview: FC<InboxOverviewProps> = ({
  arePagesLoading = false,
  fromDate,
  pages,
  toDate,
}) => {
  // Aggregate across all inbox pages (no pageId) — only list platform tabs that
  // actually have data.
  const { data: availablePlatforms } = useGetInboxAvailablePlatformsQuery({
    fromDate,
    toDate,
  })
  const platformOptions = PLATFORM_OPTIONS.filter(
    (platform) =>
      platform.id === 'all' || (availablePlatforms ?? []).includes(platform.id)
  )
  const [activePlatform, setActivePlatform] = useState('all')
  const [comparisonMetric, setComparisonMetric] = useState<
    'chats' | 'messages'
  >('messages')
  const { data: overviewSummary, isFetching: summaryFetching } =
    useGetInboxOverviewSummaryQuery({
      fromDate,
      platform: activePlatform,
      toDate,
    })

  const filteredPages = useMemo(() => {
    if (activePlatform === 'all') return pages
    return pages.filter((page) =>
      page.platforms.some((platform) => platform === activePlatform)
    )
  }, [activePlatform, pages])

  const isAllPlatformsSelected = activePlatform === 'all'
  const activePlatformLabel = getPlatformLabel(activePlatform)
  // The API summary is the source of truth whenever it is available OR on its
  // way. `aggregateTotals(filteredPages, ...)` below is a client-side estimate
  // over the page list and produces DIFFERENT numbers, so letting it render
  // while a request is in flight is what made Total Chats / Total Messages
  // change twice on the first visit to a platform tab (a revisit was served
  // from cache, so the flicker looked intermittent). It stays only as the
  // fallback for when the endpoint is genuinely unavailable.
  const useSummaryData = Boolean(overviewSummary) || summaryFetching

  // Summary-only cards (Top Topic / Top Campaigns / Top Source / Response Time)
  // derive from `overviewSummary` alone, so before the first response lands they
  // have an empty array or null and previously rendered "No Data" — asserting
  // there is none while the request was still in flight. Skeletons cover the
  // FIRST load only: the query keeps its previous data across a key change
  // (placeholderData: keepPreviousData), so a date or platform switch leaves
  // `overviewSummary` populated and the real values stay on screen.
  const isInitialLoading =
    (summaryFetching && !overviewSummary) || arePagesLoading

  const organicVsPaid = overviewSummary?.organicVsPaidChats ?? null
  const newVsReturning = overviewSummary?.newVsReturningUsers ?? null
  // Trend badges only come from the API: the client-side page aggregate has no
  // previous period to compare against.
  const comparison = overviewSummary?.comparison ?? null
  const newSources = comparison?.sources.newSources ?? []

  const chatTotals = useMemo(
    () =>
      useSummaryData
        ? mapApiSentimentTotals(overviewSummary?.totalChats)
        : aggregateTotals(filteredPages, 'conversations'),
    [filteredPages, overviewSummary?.totalChats, useSummaryData]
  )

  const messageTotals = useMemo(
    () =>
      useSummaryData
        ? mapApiSentimentTotals(overviewSummary?.totalMessages)
        : aggregateTotals(filteredPages, 'messages'),
    [filteredPages, overviewSummary?.totalMessages, useSummaryData]
  )

  const sources = useMemo(() => {
    if (useSummaryData) {
      return (overviewSummary?.sources.items ?? []).map((source) => ({
        color: PLATFORM_COLORS[source.id] ?? '#8080FF',
        id: source.id,
        label: source.name,
        value: source.chats,
      }))
    }

    const sourceMap = new Map<string, number>()

    filteredPages.forEach((page) => {
      page.platforms.forEach((platform) => {
        sourceMap.set(
          platform,
          (sourceMap.get(platform) ?? 0) + page.conversations.total
        )
      })
    })

    return Array.from(sourceMap.entries()).map(([id, value]) => ({
      color: PLATFORM_COLORS[id] ?? '#8080FF',
      id,
      label: getPlatformLabel(id),
      value,
    }))
  }, [filteredPages, overviewSummary?.sources.items, useSummaryData])

  const comparisonItems = useMemo(() => {
    if (useSummaryData) {
      const metric = isAllPlatformsSelected ? comparisonMetric : 'messages'
      return (overviewSummary?.platformComparison.items ?? []).map((source) =>
        mapApiSourceToComparisonItem(source, metric)
      )
    }

    if (!isAllPlatformsSelected) {
      const metricKey = 'messages'
      const selectedTotals = aggregateTotals(filteredPages, metricKey)
      const allTotals = aggregateTotals(pages, metricKey)
      const divisor = Math.max(PLATFORM_OPTIONS.length - 1, 1)
      const allPlatformAverage = {
        negative: Math.round(allTotals.negative / divisor),
        neutral: Math.round(allTotals.neutral / divisor),
        positive: Math.round(allTotals.positive / divisor),
        total: Math.round(allTotals.total / divisor),
      }

      return [
        {
          id: activePlatform,
          label: activePlatformLabel,
          ...selectedTotals,
        },
        {
          id: 'all-platforms',
          label: 'All platforms',
          ...allPlatformAverage,
        },
      ]
    }

    const platformMap = new Map<string, SentimentTotals>()
    const metricKey =
      comparisonMetric === 'chats' ? 'conversations' : 'messages'

    filteredPages.forEach((page) => {
      page.platforms.forEach((platform) => {
        const current = platformMap.get(platform) ?? { ...emptyTotals }
        const totals = page[metricKey]

        platformMap.set(platform, {
          negative: current.negative + totals.negative,
          neutral: current.neutral + totals.neutral,
          positive: current.positive + totals.positive,
          total: current.total + totals.total,
        })
      })
    })

    return Array.from(platformMap.entries()).map(([id, totals]) => ({
      id,
      label: getPlatformLabel(id),
      ...totals,
    }))
  }, [
    activePlatform,
    activePlatformLabel,
    comparisonMetric,
    filteredPages,
    isAllPlatformsSelected,
    overviewSummary?.platformComparison.items,
    pages,
    useSummaryData,
  ])

  const topTopicItems = useMemo(() => {
    if (!useSummaryData) return []

    return (overviewSummary?.topTopics ?? []).map((topic) => ({
      messages: topic.messages,
      negative: topic.sentiment.negative.percentage,
      neutral: topic.sentiment.neutral.percentage,
      positive: topic.sentiment.positive.percentage,
      title: topic.title,
    }))
  }, [overviewSummary?.topTopics, useSummaryData])

  const topCampaignItems = useMemo(() => {
    if (!useSummaryData) return []

    const campaigns = overviewSummary?.topCampaigns ?? []
    const maxChats = Math.max(...campaigns.map((campaign) => campaign.chats), 1)
    const maxMessages = Math.max(
      ...campaigns.map((campaign) => campaign.messages),
      1
    )

    return campaigns.map((campaign) => ({
      chats: campaign.chats,
      chatsProgress: Math.round((campaign.chats / maxChats) * 100),
      messages: campaign.messages,
      messagesProgress: Math.round((campaign.messages / maxMessages) * 100),
      title: campaign.title,
    }))
  }, [overviewSummary?.topCampaigns, useSummaryData])

  const responseTimeBreakdown = useMemo(() => {
    if (!useSummaryData) return []

    return (overviewSummary?.responseTimeBreakdown ?? []).map((bucket) => ({
      color:
        bucket.id === 'under_1m'
          ? SENTIMENT_COLORS.positive
          : bucket.id === 'one_to_five_min'
            ? SENTIMENT_COLORS.neutral
            : SENTIMENT_COLORS.negative,
      label: bucket.label,
      percentage: bucket.percentage,
    }))
  }, [overviewSummary?.responseTimeBreakdown, useSummaryData])

  const topSource = useMemo(() => {
    if (!useSummaryData || !overviewSummary?.topSource) {
      return null
    }

    const source = overviewSummary.topSource
    const topicClasses: TopSourceTopic['className'][] = [
      'positiveTopicPill',
      'negativeTopicPill',
      'neutralTopicPill',
    ]

    return {
      chats: source.chats,
      negative: source.sentiment.negative.percentage,
      neutral: source.sentiment.neutral.percentage,
      positive: source.sentiment.positive.percentage,
      sourceId: source.id ?? source.name,
      sourceName: source.name,
      sourceRankLabel: source.rankLabel,
      stats: [
        { label: 'Chats', value: source.chats.toLocaleString() },
        { label: 'Messages', value: source.messages.toLocaleString() },
        {
          label: 'Avg msg/chat',
          value: source.avgMessagesPerChat.toLocaleString(),
        },
        {
          label: 'Avg resp. time',
          value: formatResponseTime(source.avgResponseTime),
        },
      ],
      topics: source.mostMentionedTopics.map((topic, index) => ({
        className: topicClasses[index % topicClasses.length],
        label: topic.name,
      })),
    }
  }, [overviewSummary?.topSource, useSummaryData])

  const sourceChartOptions: EChartOption<EChartOption.SeriesPie> =
    useMemo(() => {
      const sourceSeries = {
        data: sources.map((source) => ({
          name: source.label,
          value: source.value || 1,
        })),
        emphasis: {
          scale: false,
        },
        itemStyle: {
          borderColor: '#F8F9FF',
          borderWidth: 1,
        },
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        radius: ['74%', '100%'],
        type: 'pie',
      } as EChartOption.SeriesPie

      return {
        color: sources.map((source) => source.color),
        series: [sourceSeries],
        tooltip: {
          formatter: '{b}: {c}',
          trigger: 'item',
        },
      }
    }, [sources])

  return (
    <section className={classes.overview}>
      <div className={classes.overviewHeader}>
        <h2 className={classes.overviewTitle}>Inbox Overview</h2>
        <div className={classes.platformTabs}>
          {platformOptions.map((platform) => (
            <button
              className={`${classes.platformTab} ${
                activePlatform === platform.id ? classes.active : ''
              }`}
              key={platform.id}
              onClick={() => setActivePlatform(platform.id)}
              type="button"
            >
              {platform.label}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.metricGrid}>
        <MetricCard
          badge={
            <TrendBadge
              previousFromDate={comparison?.previousFromDate}
              previousToDate={comparison?.previousToDate}
              value={comparison?.totalChats.changePercent}
            />
          }
          icon={TotalChatsIcon}
          isLoading={isInitialLoading}
          title="Total Chats"
          totals={chatTotals}
        />
        <MetricCard
          badge={
            <TrendBadge
              previousFromDate={comparison?.previousFromDate}
              previousToDate={comparison?.previousToDate}
              value={comparison?.totalMessages.changePercent}
            />
          }
          icon={TotalMessagesIcon}
          isLoading={isInitialLoading}
          title="Total Messages"
          totals={messageTotals}
        />

        {isAllPlatformsSelected ? (
          <div className={classes.metricCard}>
            <div className={classes.cardHeader}>
              <div className={classes.cardTitle}>
                <TotalSourceIcon className={classes.cardIcon} />
                <span>Total Sources</span>
              </div>
              <div className={classes.cardValueGroup}>
                {newSources.length > 0 && (
                  <TrendBadge
                    label={`${newSources.length} new source${
                      newSources.length === 1 ? '' : 's'
                    }`}
                    previousFromDate={comparison?.previousFromDate}
                    previousToDate={comparison?.previousToDate}
                    value={newSources.length}
                  />
                )}
                <strong className={classes.cardValue}>{sources.length}</strong>
              </div>
            </div>

            <div className={classes.sourcesContent}>
              <div className={classes.sourcesList}>
                {sources.map((source) => (
                  <div className={classes.sourceItem} key={source.id}>
                    {getPlatformIcon(source.id)}
                    <span className={classes.sourceName}>{source.label}</span>
                    <span
                      className={classes.sourceDot}
                      style={{ backgroundColor: source.color }}
                    />
                  </div>
                ))}
              </div>

              <div className={classes.sourceChart}>
                <div className={classes.sourceChartLabel}>
                  <span>Chats</span>
                  <strong>{chatTotals.total.toLocaleString()}</strong>
                </div>
                <EChart height={56} options={sourceChartOptions} width={56} />
              </div>
            </div>
          </div>
        ) : (
          <ResponseTimeBreakdownCard
            isLoading={isInitialLoading}
            items={responseTimeBreakdown}
          />
        )}
      </div>

      {/* Second row, directly under Total Chats / Messages / Sources:
          Organic vs Paid Chats -> Total Users -> New vs Returning Users */}
      <div className={`${classes.metricGrid} ${classes.audienceGrid}`}>
        <SplitMetricCard
          icon={TotalChatsIcon}
          isLoading={isInitialLoading}
          segments={
            organicVsPaid
              ? [
                  {
                    color: AUDIENCE_COLORS.organic,
                    count: organicVsPaid.organic,
                    label: 'Organic',
                  },
                  {
                    color: AUDIENCE_COLORS.paid,
                    count: organicVsPaid.paid,
                    label: 'Paid',
                  },
                ]
              : []
          }
          title="Organic vs Paid Chats"
          total={organicVsPaid?.total ?? null}
        />
        <SplitMetricCard
          badge={
            <TrendBadge
              previousFromDate={comparison?.previousFromDate}
              previousToDate={comparison?.previousToDate}
              value={comparison?.totalUsers.changePercent}
            />
          }
          icon={TotalUsersIcon}
          isLoading={isInitialLoading}
          title="Total Users"
          total={overviewSummary?.totalUsers?.total ?? null}
        />
        <SplitMetricCard
          icon={NewVsReturningIcon}
          isLoading={isInitialLoading}
          segments={
            newVsReturning
              ? [
                  {
                    color: AUDIENCE_COLORS.new,
                    count: newVsReturning.new,
                    label: 'New',
                  },
                  {
                    color: AUDIENCE_COLORS.returning,
                    count: newVsReturning.returning,
                    label: 'Returning',
                  },
                ]
              : []
          }
          title="New vs Returning Users"
          total={newVsReturning?.total ?? null}
        />
      </div>

      <div className={classes.analysisGrid}>
        <div
          className={`${classes.analysisCard} ${classes.topTopicCard} ${
            comparisonItems.length || isInitialLoading ? '' : classes.emptyCard
          }`}
        >
          <div className={classes.analysisHeader}>
            <div className={classes.analysisTitle}>
              <PlatformComparisonIcon className={classes.analysisIcon} />
              <span>
                {isAllPlatformsSelected
                  ? 'Platform Comparison'
                  : `${activePlatformLabel} vs. All-Platform Average`}
              </span>
            </div>
            {isAllPlatformsSelected && (
              <div className={classes.metricToggle}>
                <button
                  className={`${classes.metricToggleButton} ${
                    comparisonMetric === 'chats' ? classes.active : ''
                  }`}
                  onClick={() => setComparisonMetric('chats')}
                  type="button"
                >
                  Chats
                </button>
                <button
                  className={`${classes.metricToggleButton} ${
                    comparisonMetric === 'messages' ? classes.active : ''
                  }`}
                  onClick={() => setComparisonMetric('messages')}
                  type="button"
                >
                  Messages
                </button>
              </div>
            )}
          </div>
          <div
            className={`${classes.analysisBody} ${classes.platformComparisonBody}`}
          >
            {isInitialLoading ? (
              <Skeleton
                height={PLATFORM_CHART_CARD_HEIGHT}
                radius={PLATFORM_CHART_CARD_RADIUS}
              />
            ) : comparisonItems.length ? (
              <PlatformComparisonChart
                items={comparisonItems}
                metricLabel={
                  comparisonMetric === 'chats' ? 'Chats' : 'Messages'
                }
              />
            ) : (
              <div className={classes.overviewNoData}>No Data</div>
            )}
          </div>
        </div>

        <div
          className={`${classes.analysisCard} ${
            topTopicItems.length || isInitialLoading ? '' : classes.emptyCard
          }`}
        >
          <div className={classes.analysisHeader}>
            <div className={classes.analysisTitle}>
              <TopicIcon className={classes.analysisIcon} />
              <span>Top Topic</span>
            </div>
          </div>
          <div className={`${classes.analysisBody} ${classes.topTopicBody}`}>
            {isInitialLoading ? (
              <TopTopicSkeleton />
            ) : topTopicItems.length ? (
              <TopTopicList items={topTopicItems} />
            ) : (
              <div className={classes.overviewNoData}>No Data</div>
            )}
          </div>
        </div>
      </div>

      <div className={classes.bottomGrid}>
        <div
          className={`${classes.bottomCard} ${
            topCampaignItems.length || isInitialLoading ? '' : classes.emptyCard
          }`}
        >
          <div className={classes.bottomHeader}>
            <div className={classes.bottomTitle}>
              <TopCampaignsIcon className={classes.bottomHeaderIcon} />
              <span>Top Campaigns</span>
              <span className={classes.bottomSubtitle}>
                — by message & chat volume
              </span>
            </div>
          </div>
          <div className={classes.campaignLegend}>
            <span>
              <i className={classes.messagesLegend} />
              Messages
            </span>
            <span>
              <i className={classes.chatsLegend} />
              Chats
            </span>
          </div>
          <div className={`${classes.bottomBody} ${classes.topCampaignsBody}`}>
            {isInitialLoading ? (
              <TopCampaignsSkeleton />
            ) : topCampaignItems.length ? (
              <TopCampaignsList items={topCampaignItems} />
            ) : (
              <div className={classes.overviewNoData}>No Data</div>
            )}
          </div>
        </div>

        <div
          className={`${classes.bottomCard} ${
            topSource || isInitialLoading ? '' : classes.emptyCard
          }`}
        >
          <div className={classes.bottomHeader}>
            <div className={classes.bottomTitle}>
              <GlobeIcon className={classes.bottomHeaderIcon} />
              <span>Top Source</span>
            </div>
          </div>
          <div className={classes.bottomBody}>
            {isInitialLoading ? (
              <TopSourceSkeleton />
            ) : topSource ? (
              <TopSourceDetails data={topSource} />
            ) : (
              <div className={classes.overviewNoData}>No Data</div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
